import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import { getTranslation } from '../../../utils';

const FrameRangeSlider = ({
  videoURL,
  videoDuration,
  onRangeChange,
  frameCount = 20,
  orgPrefix
}) => {
  const { t } = useTranslation();

  const [frames, setFrames] = useState([]);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
  const [rangeValue, setRangeValue] = useState([0, videoDuration]);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current || !videoURL || !videoDuration) return;

    const video = videoRef.current;
    const frameImages = [];
    const interval = videoDuration / frameCount;

    const loadFrame = (index) => {
      return new Promise((resolve) => {
        const time = Math.min(index * interval, videoDuration - 0.1);
        video.currentTime = time;

        const onSeeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const dataURL = canvas.toDataURL('image/jpeg', 1);
            frameImages[index] = {
              src: dataURL,
              time: time,
              width: video.videoWidth,
              height: video.videoHeight
            };
          } catch (error) {
            console.error('Error extracting frame:', error);
          }
          video.removeEventListener('seeked', onSeeked);
          resolve();
        };

        video.addEventListener('seeked', onSeeked, { once: true });
      });
    };

    const loadAllFrames = async () => {
      setIsLoading(true);

      const onMetadataLoaded = async () => {
        setVideoDimensions({
          width: video.videoWidth,
          height: video.videoHeight
        });

        // Load frames sequentially to avoid conflicts
        for (let i = 0; i < frameCount; i++) {
          await loadFrame(i);
        }

        setFrames(frameImages.filter(Boolean));
        setIsLoading(false);
      };

      if (video.readyState >= 1) {
        await onMetadataLoaded();
      } else {
        video.addEventListener('loadedmetadata', onMetadataLoaded, { once: true });
      }
    };

    loadAllFrames();
  }, [videoURL, videoDuration, frameCount]);

  useEffect(() => {
    setRangeValue([0, videoDuration]);
  }, [videoDuration]);

  const handleRangeChange = (value) => {
    setRangeValue(value);
    if (onRangeChange) {
      onRangeChange(value);
    }
  };

  const aspectRatio =
    videoDimensions.height > 0 ? videoDimensions.width / videoDimensions.height : 9 / 16;

  const frameHeight = 80;
  const frameWidth = frameHeight * aspectRatio;

  return (
    <div
      ref={containerRef}
      className="frame-range-slider-container"
      style={{
        position: 'relative',
        width: '100%',
        marginTop: '16px'
      }}>
      {/* Hidden video element for frame extraction */}
      <video
        ref={videoRef}
        src={videoURL}
        style={{ display: 'none' }}
        preload="metadata"
        crossOrigin="anonymous"
      />

      {/* Frames display */}
      <div
        className="frames-container"
        style={{
          display: 'flex',
          width: '100%',
          height: `${frameHeight}px`,
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#000',
          borderRadius: '4px',
          minHeight: `${frameHeight}px`,
          alignItems: 'stretch'
        }}>
        {isLoading ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff'
            }}>
            {getTranslation(t, 'video.trimmer.loadingFrames', orgPrefix)}
          </div>
        ) : (
          frames.map((frame, index) => (
            <img
              key={index}
              src={frame?.src}
              alt={`Frame ${index}`}
              style={{
                width: `${frameWidth}px`,
                height: `${frameHeight}px`,
                objectFit: 'cover',
                flexShrink: 0,
                display: 'block',
                minWidth: `${frameWidth}px`
              }}
            />
          ))
        )}
      </div>

      {/* Range slider overlay */}
      {!isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: `${frameHeight}px`,
            pointerEvents: 'auto'
          }}>
          <RangeSlider
            min={0}
            max={videoDuration}
            step={videoDuration / 1000}
            value={rangeValue}
            onInput={handleRangeChange}
            className="frame-range-slider !bg-transparent !h-full"
          />
        </div>
      )}
    </div>
  );
};

export default FrameRangeSlider;
